import React, { useEffect, useState } from "react";
import { ledgerApi, DEV_MODE } from "../lib/ledgerApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import { Wrench, AlertTriangle, RefreshCw } from "lucide-react";

export default function Dev() {
  const [blockNumber, setBlockNumber] = useState("");
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [busy, setBusy] = useState(false);

  const refreshVerify = async () => {
    setVerifying(true);
    try {
      const v = await ledgerApi.verify();
      setVerify(v);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    refreshVerify();
    const i = setInterval(refreshVerify, 4000);
    return () => clearInterval(i);
  }, []);

  if (!DEV_MODE) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <div className="text-status-tampered text-xl mb-2">DEV_MODE is off</div>
        <p className="text-sm text-muted-foreground">
          Set <code className="font-mono">REACT_APP_DEV_MODE=true</code> in the frontend
          and <code className="font-mono">DEV_MODE=true</code> on the backend, then restart.
        </p>
      </div>
    );
  }

  const onTamper = async () => {
    setBusy(true);
    try {
      const n = blockNumber === "" ? null : Number(blockNumber);
      const out = await ledgerApi.tamper(n);
      toast.success("Tampered", {
        description: `Block #${out.tampered_block}: ${out.field} corrupted`,
      });
      refreshVerify();
    } catch (e) {
      toast.error("Tamper failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    if (!window.confirm("This will wipe ALL blocks/transactions/wallets and re-seed. Continue?")) return;
    setBusy(true);
    try {
      await ledgerApi.reset();
      toast.success("Chain reset", { description: "Genesis + seed re-created" });
      refreshVerify();
    } catch (e) {
      toast.error("Reset failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-8 py-10 md:py-14">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-status-tampered mb-3">/ Dev tools</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <Wrench className="w-6 h-6 text-status-tampered" strokeWidth={1.5} />
            Tamper & verify
          </h1>
        </div>
        <StatusBadge status={verify?.status} loading={verifying && !verify} testid="dev-status-badge" />
      </div>

      <div className="border border-status-tampered/40 bg-status-tampered/5 p-5 rounded-sm mb-8 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-status-tampered shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="text-sm">
          <div className="font-medium text-status-tampered">Dev-only utilities</div>
          <div className="text-muted-foreground text-xs mt-1">
            These endpoints corrupt chain state on purpose so you can demonstrate that
            <code className="font-mono mx-1">/api/ledger/verify</code>
            detects tampering. Disable by setting
            <code className="font-mono mx-1">DEV_MODE=false</code>.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {/* Tamper card */}
        <div className="border border-border rounded-sm bg-card p-5 sm:p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Action</div>
          <h2 className="text-lg font-medium mb-1">Tamper with a block</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Corrupts <code className="font-mono">merkle_root</code> on the chosen block (or the latest if blank).
          </p>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Block number (optional)</Label>
          <Input
            data-testid="tamper-block-number"
            type="number"
            min="0"
            value={blockNumber}
            onChange={(e) => setBlockNumber(e.target.value)}
            placeholder="leave blank → latest"
            className="rounded-sm font-mono mt-1"
          />
          <Button
            onClick={onTamper}
            disabled={busy}
            data-testid="tamper-button"
            className="mt-4 rounded-sm bg-status-tampered text-white hover:bg-status-tampered/90"
          >
            <AlertTriangle className="w-4 h-4 mr-1" strokeWidth={1.5} /> Tamper
          </Button>
        </div>

        {/* Reset card */}
        <div className="border border-border rounded-sm bg-card p-5 sm:p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Action</div>
          <h2 className="text-lg font-medium mb-1">Reset chain</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Wipes blocks, txns, and wallets. Re-creates genesis + Alice/Bob/Carol with 1000 NEO each.
          </p>
          <Button
            onClick={onReset}
            disabled={busy}
            data-testid="reset-chain-button"
            variant="outline"
            className="rounded-sm border-border hover:border-foreground"
          >
            <RefreshCw className="w-4 h-4 mr-1" strokeWidth={1.5} /> Reset & re-seed
          </Button>
        </div>
      </div>

      {/* Verify result */}
      {verify && (
        <div className="mt-10">
          <h2 className="text-lg font-medium mb-3">Verify report</h2>
          <div className="border border-border rounded-sm bg-card divide-y divide-border" data-testid="verify-report">
            {verify.details.map((d) => (
              <div
                key={d.block_number}
                className={`p-3 grid grid-cols-12 gap-3 items-center font-mono text-xs ${
                  !d.ok ? "bg-status-tampered/10 text-status-tampered" : ""
                }`}
              >
                <div className="col-span-2">#{d.block_number}</div>
                <div className="col-span-2 uppercase tracking-widest">{d.ok ? "ok" : "bad"}</div>
                <div className="col-span-8">{d.reason || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
